import React, { useState } from 'react';
import { 
  Container, 
  Typography, 
  Paper, 
  Button, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Alert,
  Box,
  Divider,
  CircularProgress
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { exportTransactions } from '../../../services/transactionService';
import { useGroupContext } from '../../Groups/useGroupContext';

const TransactionExportPage = () => {
  const [transactionType, setTransactionType] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const { getContextParams, isGroupMode, currentGroupName } = useGroupContext();

  const handleExport = async () => {
    setIsLoading(true);
    setError(null);
    setSuccess(false);
    
    try {
      const contextParams = getContextParams();
      await exportTransactions(transactionType, contextParams);
      setSuccess(true);
    } catch (err) {
      setError('Failed to export transactions. Please try again.');
      console.error('Export error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom>
          Export Transactions {isGroupMode && `- ${currentGroupName}`}
        </Typography>
        
        <Paper sx={{ p: 4, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Download Your Transaction Data
          </Typography>
          
          <Typography variant="body1" paragraph>
            Export your {isGroupMode ? 'group' : ''} transactions as an Excel file (.xlsx) that you can open in Microsoft Excel, Google Sheets, or other spreadsheet applications.
          </Typography>
          
          {isGroupMode && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Exporting transactions from group: <strong>{currentGroupName}</strong>
            </Alert>
          )}
          
          <Divider sx={{ my: 3 }} />
          
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="transaction-type-label">Transaction Type (Optional)</InputLabel>
            <Select
              labelId="transaction-type-label"
              value={transactionType}
              label="Transaction Type (Optional)"
              onChange={(e) => setTransactionType(e.target.value)}
            >
              <MenuItem value="">All Transactions</MenuItem>
              <MenuItem value="EXPENSE">Expenses Only</MenuItem>
              <MenuItem value="INCOME">Income Only</MenuItem>
              <MenuItem value="INVESTMENT">Investments Only</MenuItem>
            </Select>
          </FormControl>
          
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              Your transactions have been successfully exported!
            </Alert>
          )}
          
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={isLoading ? <CircularProgress size={24} color="inherit" /> : <FileDownloadIcon />}
            onClick={handleExport}
            disabled={isLoading}
            fullWidth
          >
            {isLoading ? 'Exporting...' : 'Export Transactions'}
          </Button>
          
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
            The file will contain these columns: date, vendor, amount, category, note
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};

export default TransactionExportPage;